import { useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, AlertCircle } from "lucide-react";
import AuthLayout from "@/components/layouts/AuthLayout";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { authApi } from "@/services/api";
import { config } from "@/config";

const forgotPasswordSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email address" }),
});

type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;

const ForgotPassword = () => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showTroubleshooting, setShowTroubleshooting] = useState(false);
  const [showDebugDialog, setShowDebugDialog] = useState(false);
  const [debugInfo, setDebugInfo] = useState<any>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordFormValues) => {
    setIsLoading(true);
    setError(null);
    
    try {
      console.log("Sending password reset email to:", data.email);
      
      // Get the current origin for the redirectTo URL
      const origin = window.location.origin || config.CLIENT_URL;
      const redirectUrl = `${origin}/auth/set-password`;
      
      console.log("Using redirectUrl:", redirectUrl);
      
      // Add debug info about the environment
      setDebugInfo({
        useMethod: 'auth-api',
        email: data.email,
        redirectUrl: redirectUrl,
        origin: window.location.origin,
        configClientUrl: config.CLIENT_URL,
        fullUrl: window.location.href
      });
      
      const response = await authApi.requestPasswordReset(data.email);
      
      console.log("Password reset request response:", response);
      
      setIsSubmitted(true);
      
      toast({
        title: "Reset link sent",
        description: `We've sent a password reset link to ${data.email}`,
      });
    } catch (error: any) {
      console.error("Password reset error:", error);
      setError(error.message || "An error occurred. Please try again.");
      
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "An error occurred. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetry = () => {
    setIsSubmitted(false);
    setError(null);
  };

  return (
    <AuthLayout 
      title="Reset your password" 
      subtitle="Enter your email and we'll send you a reset link"
    >
      {!isSubmitted ? (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="your@email.com"
              {...register("email")}
            />
            {errors.email && (
              <p className="text-sm text-red-500">{errors.email.message}</p>
            )}
          </div>

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending reset link...
              </>
            ) : (
              "Send reset link"
            )}
          </Button>

          <div className="text-sm text-center text-gray-500">
            Remember your password?{" "}
            <Link to="/auth/login" className="font-medium text-brand-blue hover:underline">
              Sign in
            </Link>
          </div>
        </form>
      ) : (
        <div className="text-center">
          <div className="p-6">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <svg
                className="h-6 w-6 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h3 className="mt-4 text-lg font-medium text-gray-900">
              Check your email
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              We've sent a password reset link to {getValues("email")}.
            </p>
            <p className="mt-3 text-xs text-gray-500">
              If you don't see the email in your inbox, please check your spam folder.
              The email should arrive within a few minutes.
            </p>
            
            <Button 
              variant="link" 
              className="mt-2 text-xs text-gray-500 underline"
              onClick={() => setShowTroubleshooting(!showTroubleshooting)}
            >
              {showTroubleshooting ? "Hide troubleshooting tips" : "Not receiving emails?"}
            </Button>
            
            {showTroubleshooting && (
              <div className="mt-2 text-left p-4 bg-gray-50 rounded-md">
                <h4 className="text-sm font-medium">Troubleshooting Tips:</h4>
                <ul className="list-disc pl-5 mt-2 text-xs text-gray-600 space-y-1">
                  <li>Check your spam or junk folder</li>
                  <li>Add Dommek &lt;no-reply@dommek.com&gt; to your contacts</li>
                  <li>Verify that you entered the correct email address</li>
                  <li>Try using a different email provider</li>
                  <li>Contact support if the issue persists</li>
                </ul>
              </div>
            )}
          </div>
          <div className="mt-4">
            <Button asChild variant="outline" className="w-full">
              <Link to="/auth/login">Return to sign in</Link>
            </Button>
          </div>
          <Button
            variant="link"
            className="mt-2 text-xs"
            onClick={handleRetry}
          >
            Try again with a different email
          </Button>
          
          <div className="mt-4">
            <Button 
              variant="link" 
              size="sm" 
              className="text-xs text-gray-400"
              onClick={() => setShowDebugDialog(true)}
            >
              Debug Info
            </Button>
          </div>
        </div>
      )}
      
      <Dialog open={showDebugDialog} onOpenChange={setShowDebugDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Password Reset Debug Info</DialogTitle>
            <DialogDescription>
              Technical information about the password reset attempt
            </DialogDescription>
          </DialogHeader>
          <div className="bg-gray-100 p-4 rounded-md overflow-auto max-h-[300px]">
            <pre className="text-xs text-gray-800">
              {JSON.stringify(debugInfo, null, 2)}
            </pre>
          </div>
          <Button onClick={() => setShowDebugDialog(false)}>Close</Button>
        </DialogContent>
      </Dialog>
    </AuthLayout>
  );
};

export default ForgotPassword;
